import { forwardRef } from 'react';
import { clsx } from 'clsx';

/**
 * Campo de data com máscara DD/MM/YYYY.
 * Valor interno e externo: string no formato YYYY-MM-DD (ISO).
 * Display: texto mascarado DD/MM/YYYY com navegador de data nativo.
 *
 * Compatível com react-hook-form via register():
 *   <DatePicker label="Data" {...register('date')} error={errors.date?.message} />
 *
 * Também suporta uso controlado:
 *   <DatePicker value={value} onChange={(e) => setValue(e.target.value)} />
 */
const DatePicker = forwardRef(({
  label,
  error,
  hint,
  fullWidth = true,
  containerClassName,
  className,
  required,
  ...props
}, ref) => {
  return (
    <div className={clsx(fullWidth && 'w-full', containerClassName)}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      <input
        ref={ref}
        type="date"
        className={clsx(
          'block w-full rounded-lg border transition-colors duration-200',
          'focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500',
          'disabled:bg-gray-100 disabled:cursor-not-allowed dark:disabled:bg-gray-800',
          'dark:bg-gray-800 dark:border-gray-600 dark:text-gray-100',
          'pl-4 pr-4 py-2',
          // Garante que o ícone nativo do calendário fique visível no dark mode
          '[color-scheme:light] dark:[color-scheme:dark]',
          error
            ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20'
            : 'border-gray-300 dark:border-gray-600',
          className
        )}
        {...props}
      />

      {error && (
        <p className="mt-1 text-sm text-red-500">{error}</p>
      )}

      {hint && !error && (
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{hint}</p>
      )}
    </div>
  );
});

DatePicker.displayName = 'DatePicker';

export default DatePicker;
